<template>
  <ais-range-input
    v-if="isPrice"
    :attribute="attribute"
    class="algolia-facet__range-slider"
  >
    <template #default="{ currentRefinement, range, refine }">
      <div
        v-show="range.min !== range.max"
        v-click-outside="handleClickOutside"
        class="algolia-facet"
        :class="{[$class('active')]: open}"
      >
        <button
          class="algolia-facet__control"
          type="button"
          @click="toggleOpen"
        >
          <span
            class="text-label-xs"
            v-text="label || ''"
          />
          <icon-chevron-down />
        </button>

        <transition
          name="fade-down"
          mode="out-in"
        >
          <div
            v-show="open"
            class="algolia-facet__content algolia-facet__content--price"
          >
            <algolia-range-slider
              :min="range.min"
              :max="range.max"
              :value="toValue(currentRefinement, range)"
              @algolia-range-updated="(value) => refine({ min: value.min, max: value.max })"
            />

            <hr class="algolia-facet__divider">
            <div class="algolia-facet__footer">
              <p
                class="text-label-xs"
                v-text="$string('collection.highest_price', {
                  replace: {
                    price: $formatMoney(range.max * 100),
                  },
                })"
              />

              <button
                class="algolia-facet__control"
                type="button"
                @click="() => refine({ min: range.min, max: range.max })"
              >
                <span
                  class="text-label-xs"
                  v-text="$string('collection.reset')"
                />
              </button>
            </div>
          </div>
        </transition>
      </div>
    </template>
  </ais-range-input>
  <div
    v-else-if="filters.length && !isPrice"
    v-click-outside="handleClickOutside"
    class="algolia-facet"
    :class="{[$class('active')]: open}"
  >
    <button
      class="algolia-facet__control"
      type="button"
      @click="toggleOpen"
    >
      <span
        class="text-label-xs"
        v-text="label || ''"
      />
      <icon-chevron-down />
    </button>

    <transition
      name="fade-down"
      mode="out-in"
    >
      <div
        v-show="open"
        class="algolia-facet__content"
      >
        <ul
          class="algolia-facet__content-inner"
          :class="listClass"
        >
          <li
            v-for="facet in filters"
            :key="facet.value"
          >
            <algolia-facet-value
              v-if="facet.value"
              :is-colour="isColour"
              :facet="facet"
              :active="open"
              @click="$emit('choices-updated', facet.value)"
            />
          </li>
        </ul>
        <hr class="algolia-facet__divider">
        <div class="algolia-facet__footer">
          <p
            class="text-label-xs"
            v-text="$string('collection.selected', {
              replace: {
                count: appliedFiltersCount,
              },
            })"
          />

          <button
            class="algolia-facet__control"
            type="button"
            @click="$emit('choices-reset')"
          >
            <span
              class="text-label-xs"
              v-text="$string('collection.reset')"
            />
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Algolia facet (main-collection)
 * -----------------------------------------------------------------------------
 * Facets for use with Algolia filters.
 *
 * @param {Array} facet - Component facets.
 *
 */
import { AisRangeInput } from 'vue-instantsearch/vue3/es'
import AlgoliaFacetValue from '~async/algolia-facet-value/algolia-facet-value'
import AlgoliaRangeSlider from '~async/algolia-range-slider/algolia-range-slider.vue'

import IconChevronDown from '~icons/direction/chevron/down.svg'

export default {
  name: 'AlgoliaFacet',

  components: {
    AlgoliaFacetValue,
    AlgoliaRangeSlider,
    IconChevronDown,
    AisRangeInput,
  },

  props: {
    label: {
      type: String,
      default: '',
    },
    facets: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    'choices-updated': (payload) => typeof payload === 'string',
    'choices-reset': (payload) => payload === undefined,
  },

  data() {
    return {
      open: false,
    }
  },

  computed: {

    /**
     * Compute content pane class based on number of values to show.
     * @returns {String}
     */
    listClass() {
      if (this.facets?.length > 7) {
        return 'double-column'
      }

      return ''
    },

    /**
     * Is this a facet from Price refinement list.
     */
    isPrice() {
      return this.label.toLowerCase() === 'price'
    },

    /**
     * Is this a facet from Price refinement list.
     */
    isColour() {
      return this.label.toLowerCase() === 'colour'
    },

    attribute() {
      const marketHandle = this.$variable('store.marketHandle')
      return `price.${marketHandle}.amount`
    },

    filters() {
      const preparedFiltered = []

      this.facets.forEach(facet => {
        if (this.isPrice) {
          return
        }

        preparedFiltered.push(facet)
      })

      return preparedFiltered
    },

    appliedFiltersCount() {
      if (this.isPrice) {
        return 0
      }

      return this.facets
        .filter(facet => facet.isRefined)
        .length
    },
  },

  methods: {

    /**
     * Returns an array ot 2 number the first is the minimum applied refinement
     * and the second is the maximum one. From Algolia docs:
     * https://www.algolia.com/doc/api-reference/widgets/range-slider/vue/
     *
     * @param {Object} value - current Algolia range input refinement
     * @param {Object} range - The minimum and maximum available values.
     */
    toValue(value, range) {
      return [
        typeof value.min === 'number' ? value.min : range.min,
        typeof value.max === 'number' ? value.max : range.max,
      ]
    },

    /**
     * Toggle active class on facet click.
     * @param {Object} event - Click event.
     */
    toggleOpen() {
      this.open = !this.open
    },

    /**
     * Click outside event handle.
     */
    handleClickOutside() {
      this.open = false
    },
  },
}
</script>
